import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  MAX_COMENTARIO_ANALISIS,
  MAX_INTENTOS_ANALISIS,
  TIPOS_EXAMEN_PSICO,
  type EstadoAnalisis,
  type TipoExamenPsico,
} from '@leadsphere/types';

import { Modal } from '../../components/ui/Modal.js';
import { api, ApiError, descargarArchivo } from '../../lib/api.js';

const ETIQUETA_TEST: Record<TipoExamenPsico, string> = {
  cleaver: 'Cleaver — DISC',
  lifo: 'LIFO',
  kostick: 'Kostick (PAPI)',
  terman: 'Terman-Merrill',
};

const TEXTO_INTENTOS_AGOTADOS = `Se usaron los ${MAX_INTENTOS_ANALISIS} intentos`;

const MENSAJE_BLOQUEO: Record<NonNullable<EstadoAnalisis['motivo_bloqueo']>, string> = {
  NO_DISPONIBLE: 'Análisis no disponible: falta configurar la llave',
  SIN_RESULTADOS: 'Aún no hay resultados',
  INTENTOS_AGOTADOS: TEXTO_INTENTOS_AGOTADOS,
  GENERACION_EN_CURSO: 'Generando…',
};

// Nota bajo el botón cuando la última generación falló, según su código.
const CODIGOS_CONFIGURACION = new Set(['LLAVE_INVALIDA', 'LLAVE_NO_CONFIGURADA', 'API_PETICION_INVALIDA']);

function mensajeFallo(codigo: string | null | undefined): string {
  if (codigo && CODIGOS_CONFIGURACION.has(codigo)) {
    return 'No se pudo generar por un problema de configuración. Avísale a Moisés. No se descontó ningún intento.';
  }
  if (codigo === 'RECHAZO') return 'Claude no generó este análisis. No se descontó ningún intento.';
  return 'No se pudo generar. No se descontó ningún intento.';
}

const nombreWord = (nombre: string, version: number) =>
  `Analisis_${nombre.replace(/[\\/:*?"<>|]+/g, '').trim().replace(/\s+/g, '_') || 'Aspirante'}_v${version}.docx`;

// Botón azul del análisis con IA (spec 2026-10-16 §4). La API decide qué se
// puede hacer (puede_generar / motivo_bloqueo); aquí solo se dibuja.
export function AnalisisIA({ aspiranteId, nombreAspirante }: { aspiranteId: string; nombreAspirante: string }) {
  const queryClient = useQueryClient();
  const queryKey = ['aspirante-analisis', aspiranteId];
  const [abierto, setAbierto] = useState(false);
  const [descargando, setDescargando] = useState(false);
  const [errorDescarga, setErrorDescarga] = useState<string | null>(null);

  const { data: estado, isError } = useQuery({
    queryKey,
    queryFn: () => api.get<EstadoAnalisis>(`/psicometricos/aspirantes/${aspiranteId}/analisis`),
    refetchInterval: (q) => (q.state.data?.ultima?.estado === 'generando' ? 4000 : false),
  });

  if (!estado) {
    return isError ? <span style={{ fontSize: 12, color: 'var(--muted)' }}>No se pudo consultar el análisis</span> : null;
  }

  const generando = estado.ultima?.estado === 'generando';
  const fallo = estado.ultima?.estado === 'error';
  const hayListo = estado.ultimo_listo !== null;

  const exportar = async () => {
    if (!estado.ultimo_listo) return;
    setDescargando(true);
    setErrorDescarga(null);
    try {
      await descargarArchivo(
        `/psicometricos/aspirantes/${aspiranteId}/analisis/${estado.ultimo_listo.id}/word`,
        nombreWord(nombreAspirante, estado.ultimo_listo.version),
      );
    } catch (err) {
      setErrorDescarga(err instanceof ApiError ? err.message : 'No fue posible descargar el análisis');
    } finally {
      setDescargando(false);
    }
  };

  const textoRegenerar =
    estado.intentos_restantes === null ? 'Regenerar' : `Regenerar (quedan ${estado.intentos_restantes})`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
      <div style={{ display: 'flex', gap: 8 }}>
        {generando ? (
          <button className="btn btn-primary btn-sm" disabled aria-busy="true">
            <span className="spinner-inline" aria-hidden="true" />
            Generando…
          </button>
        ) : hayListo ? (
          <>
            <button className="btn btn-primary btn-sm" onClick={exportar} disabled={descargando}>
              {descargando ? 'Descargando…' : 'Exportar análisis (Word)'}
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setAbierto(true)}
              disabled={!estado.puede_generar}
              title={estado.motivo_bloqueo ? MENSAJE_BLOQUEO[estado.motivo_bloqueo] : undefined}
            >
              {estado.motivo_bloqueo === 'INTENTOS_AGOTADOS' ? TEXTO_INTENTOS_AGOTADOS : textoRegenerar}
            </button>
          </>
        ) : (
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setAbierto(true)}
            disabled={!estado.puede_generar}
            title={estado.motivo_bloqueo ? MENSAJE_BLOQUEO[estado.motivo_bloqueo] : undefined}
          >
            {estado.puede_generar ? (fallo ? 'Reintentar análisis' : '✨ Generar análisis') : MENSAJE_BLOQUEO[estado.motivo_bloqueo!]}
          </button>
        )}
      </div>
      {fallo && !generando && (
        <span style={{ fontSize: 12, color: 'var(--danger)' }}>
          {mensajeFallo(estado.ultima?.error_codigo)}
        </span>
      )}
      {errorDescarga && <span style={{ fontSize: 12, color: 'var(--danger)' }}>{errorDescarga}</span>}
      {/* Una consulta posterior falló: lo de arriba puede estar desactualizado. */}
      {isError && (
        <span style={{ fontSize: 12, color: 'var(--danger)' }}>
          No se pudo actualizar el estado del análisis. Recarga la página si no cambia.
        </span>
      )}

      <GenerarAnalisisModal
        open={abierto}
        onClose={() => setAbierto(false)}
        aspiranteId={aspiranteId}
        estado={estado}
        esRegeneracion={hayListo}
        onGenerado={() => {
          setAbierto(false);
          void queryClient.invalidateQueries({ queryKey });
        }}
        onEstadoCambiado={() => void queryClient.invalidateQueries({ queryKey })}
      />
    </div>
  );
}

function GenerarAnalisisModal({
  open,
  onClose,
  aspiranteId,
  estado,
  esRegeneracion,
  onGenerado,
  onEstadoCambiado,
}: {
  open: boolean;
  onClose: () => void;
  aspiranteId: string;
  estado: EstadoAnalisis;
  esRegeneracion: boolean;
  onGenerado: () => void;
  onEstadoCambiado: () => void;
}) {
  const [marcados, setMarcados] = useState<Set<TipoExamenPsico>>(new Set());
  const [comentario, setComentario] = useState('');
  const [error, setError] = useState<string | null>(null);

  // La selección se arma solo al abrir: todos los calificados marcados. Si el
  // estado se refresca con el diálogo abierto, no se re-marca lo desmarcado.
  const calificadosRef = useRef(estado.tests_calificados);
  calificadosRef.current = estado.tests_calificados;

  useEffect(() => {
    if (!open) return;
    setMarcados(new Set(calificadosRef.current));
    setComentario('');
    setError(null);
  }, [open]);

  const mutation = useMutation({
    mutationFn: () =>
      api.post(`/psicometricos/aspirantes/${aspiranteId}/analisis`, {
        tests: TIPOS_EXAMEN_PSICO.filter((t) => marcados.has(t)),
        ...(esRegeneracion && comentario.trim() ? { comentario: comentario.trim() } : {}),
      }),
    onSuccess: onGenerado,
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'No fue posible iniciar el análisis');
      // 409: el estado cambió mientras el diálogo estaba abierto (otra
      // generación en curso o intentos agotados); se vuelve a pedir.
      if (err instanceof ApiError && err.status === 409) onEstadoCambiado();
    },
  });

  const alternar = (t: TipoExamenPsico) =>
    setMarcados((prev) => {
      const next = new Set(prev);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      return next;
    });

  return (
    <Modal
      open={open}
      title={esRegeneracion ? 'Regenerar análisis' : 'Generar análisis'}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={mutation.isPending}>Cancelar</button>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || marcados.size === 0}
          >
            {mutation.isPending ? 'Iniciando…' : 'Generar'}
          </button>
        </>
      }
    >
      <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>
        Marca las pruebas que aplican a este aspirante. El análisis solo usa los resultados; no se envían nombre, correo ni puesto.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {TIPOS_EXAMEN_PSICO.map((t) => {
          const calificado = estado.tests_calificados.includes(t);
          return (
            <label
              key={t}
              style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: calificado ? 'var(--heading)' : 'var(--muted)', cursor: calificado ? 'pointer' : 'not-allowed' }}
            >
              <input type="checkbox" checked={marcados.has(t)} disabled={!calificado || mutation.isPending} onChange={() => alternar(t)} />
              {ETIQUETA_TEST[t]}
              {!calificado && <span style={{ fontSize: 12 }}>(sin resultado)</span>}
            </label>
          );
        })}
      </div>
      {esRegeneracion && (
        <div className="field" style={{ marginTop: 14 }}>
          <label style={{ fontSize: 13 }}>¿Qué debe cambiar? (opcional)</label>
          <textarea
            rows={4}
            maxLength={MAX_COMENTARIO_ANALISIS}
            placeholder="Ej. las áreas de mejora quedaron muy genéricas. No escribas el nombre del aspirante."
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
            style={{ resize: 'vertical' }}
          />
        </div>
      )}
      {estado.intentos_restantes !== null && (
        <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 10 }}>
          Te quedan {estado.intentos_restantes} de {MAX_INTENTOS_ANALISIS}. Si la generación falla, no se descuenta.
        </p>
      )}
      {error && <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 12 }}>{error}</div>}
    </Modal>
  );
}
